import React, { memo, useMemo } from 'react';
import { getPaymentError } from '../utils/paymentErrors';
import './PaymentErrorModal.css';

/**
 * Drop-in replacement for the per-page "Payment Failed" modals.
 *
 * Takes the page's existing raw `message` (whatever the catch block already
 * stored) and turns it into a plain-English explanation, an AVS/billing
 * address guidance variant, and a reference code for support. Pages keep
 * their own state and handlers; only the modal markup is swapped.
 *
 * Props:
 *  - open            boolean
 *  - message         raw error text (or API body / Error)
 *  - onClose         "Try Again" handler (required)
 *  - onBack          optional secondary action (e.g. navigate(-1))
 *  - backLabel       label for the secondary action (default "Go Back")
 *  - onUpdateAddress optional; when provided and the error is a billing
 *                    address mismatch, an "Update Address" button is shown
 */
const PaymentErrorModal = ({ open, message, onClose, onBack, backLabel = 'Go Back', onUpdateAddress }) => {
  const error = useMemo(() => getPaymentError(message), [message]);

  if (!open) return null;

  const { isAddressMismatch, referenceCode } = error;

  return (
    <div className="pay-err-overlay" role="dialog" aria-modal="true" aria-labelledby="pay-err-title">
      <div className="pay-err-modal">
        <div className={`pay-err-header${isAddressMismatch ? ' pay-err-header--address' : ''}`}>
          <div className="pay-err-icon" aria-hidden="true">{isAddressMismatch ? '📍' : '❌'}</div>
          <h2 id="pay-err-title" className="pay-err-title">
            {isAddressMismatch ? 'Billing Address Mismatch' : 'Payment Failed'}
          </h2>
          <p className="pay-err-subtitle">
            {isAddressMismatch ? 'Your card was not charged' : 'Your payment could not be processed'}
          </p>
        </div>

        <div className="pay-err-body">
          <p className="pay-err-message">{error.message}</p>

          {isAddressMismatch ? (
            <ul className="pay-err-tips">
              <li>Wrong ZIP? Click <strong>Try Again</strong> and correct the <strong>Billing ZIP Code</strong> field.</li>
              {onUpdateAddress && (
                <li>Wrong street address? Click <strong>Update Address</strong>, enter it exactly as on your card statement, save, then come back and pay again.</li>
              )}
              <li>If everything is already correct, contact your card issuer to confirm the address they have on file.</li>
            </ul>
          ) : (
            <ul className="pay-err-tips">
              <li>Verify your card details are correct</li>
              <li>Check that you have sufficient funds</li>
              <li>Try a different card or payment method</li>
            </ul>
          )}

          {referenceCode && (
            <p className="pay-err-ref">
              Reference code: <span className="pay-err-ref-code">{referenceCode}</span> — please quote this if you contact us.
            </p>
          )}
        </div>

        <div className="pay-err-actions">
          {isAddressMismatch && onUpdateAddress && (
            <button type="button" className="pay-err-btn pay-err-btn--primary" onClick={onUpdateAddress}>
              Update Address
            </button>
          )}
          <button
            type="button"
            className={`pay-err-btn ${isAddressMismatch && onUpdateAddress ? 'pay-err-btn--secondary' : 'pay-err-btn--primary'}`}
            onClick={onClose}
          >
            Try Again
          </button>
          {onBack && (
            <button type="button" className="pay-err-btn pay-err-btn--secondary" onClick={onBack}>
              {backLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(PaymentErrorModal);
